<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingDateRuleFields',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    value: {
      type: Object,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const offsetDays = ref(null);

  const $refs = {
    get offsetDays() {
      return offsetDays.value;
    },
  };

  const addRule = (...args) => {
    const [id] = args;
    const obj = _.find(rules.value, { id });
    if (obj) {
      _.extend(item.value, obj.rule);
      setData('selectedRuleID', null);
    }
  };
  const onCheckChanged = (...args) => {
    if (isOffsetDaysSet.value) {
      setData('item.offset_days', 0);
    } else {
      setData('item.offset_days', null);
    }
  };

  const item = ref(null);
  const labelAlign = ref('right');
  const rule_type = ref('billing_date');
  const rules = ref(null);
  const selectedRuleID = ref(null);
  const isOffsetDaysSet = ref(null);
  const metadata = ref(null);
  const anchorOptions = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    item: item.value,
    labelAlign: labelAlign.value,
    rule_type: rule_type.value,
    rules: rules.value,
    selectedRuleID: selectedRuleID.value,
    isOffsetDaysSet: isOffsetDaysSet.value,
    metadata: metadata.value,
    anchorOptions: anchorOptions.value,
    ux: ux.value,
  }));

  const $$load_item = async () => {
    return props.value;
  };
  const $$load_rules = async () => {
    return $app.value.repo
      .get('list_object_by_type', {
        parameters: { object_type: 'billing-rule' },
      })
      .then((items) =>
        _.chain(items).filter({ rule_type: rule_type.value }).value()
      );
  };
  const $$load_isOffsetDaysSet = async () => {
    return item.value.offset_days != null;
  };
  const $$load_metadata = async () => {
    return $app.value.repo.get('ui_metadata');
  };
  const $$load_anchorOptions = async () => {
    return metadata.value.billing_date_rule_anchor_options;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'labelAlign' },
      { name: 'rule_type' },
      { name: 'rules' },
      { name: 'selectedRuleID' },
      { name: 'isOffsetDaysSet', dependsOn: ['item'] },
      { name: 'metadata' },
      { name: 'anchorOptions', dependsOn: ['metadata'] },
      { name: 'ux' },
    ],
    loaders: {
      item: $$load_item,
      rules: $$load_rules,
      isOffsetDaysSet: $$load_isOffsetDaysSet,
      metadata: $$load_metadata,
      anchorOptions: $$load_anchorOptions,
    },
    ux: {},
    dataRefs: {
      item,
      labelAlign,
      rule_type,
      rules,
      selectedRuleID,
      isOffsetDaysSet,
      metadata,
      anchorOptions,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_6705964765214521_script_dyo97av5k_value_callable = async (
    $event
  ) => {
    return addRule(selectedRuleID.value);
  };
  const act_6705964765214521_script_dyo97av5k_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6705964765214521_script_dyo97av5k_value_callable($event);
      },
    };
  };
  const act_4063326939524293_script_t6ng9s5s8_value_callable = async (
    $event
  ) => {
    return nextTick(() => onCheckChanged(isOffsetDaysSet.value));
  };
  const act_4063326939524293_script_t6ng9s5s8_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4063326939524293_script_t6ng9s5s8_value_callable($event);
      },
    };
  };

  const act_6705964765214521_script_dyo97av5k = new actionTypes.ScriptAction({
    actionArgs: act_6705964765214521_script_dyo97av5k_getActionArgs,
  });
  const inst_evh_6705964765214521_click_4828el7ej =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_script_dyo97av5k,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_6705964765214521_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_click_4828el7ej],
    event: 'click',
  });
  const act_4063326939524293_script_t6ng9s5s8 = new actionTypes.ScriptAction({
    actionArgs: act_4063326939524293_script_t6ng9s5s8_getActionArgs,
  });
  const inst_evh_4063326939524293_change_5stcaoril =
    new actionTypes.EventHandlerImpl({
      action: act_4063326939524293_script_t6ng9s5s8,
      event: 'change',
      displayName: 'script',
    });
  const inst_evh_4063326939524293_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4063326939524293_change_5stcaoril],
    event: 'change',
  });

  const actions = {
    inst_evh_6705964765214521_click_4828el7ej,
    inst_evh_6705964765214521_click,
    inst_evh_4063326939524293_change_5stcaoril,
    inst_evh_4063326939524293_change,
  };

  const evh_6705964765214521_click_4828el7ej = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click_4828el7ej.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4063326939524293_change_5stcaoril = async (event, scope) => {
    return await actions.inst_evh_4063326939524293_change_5stcaoril.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4063326939524293_change = async (event, scope) => {
    return await actions.inst_evh_4063326939524293_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div class="row mb-3" v-if="loaded">
    <div class="col" v-if="!readonly">
      <div class="input-group">
        <span class="input-group-text">
          <ItpIcon icon="material-symbols:calendar-month"> </ItpIcon>
        </span>
        <ItpFormSelect2
          name="selectedRuleID"
          v-model="selectedRuleID"
          :value="selectedRuleID"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (selectedRuleID =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :options="rules"
          valueField="id"
          textField="rule.description"
          :placeholder="
            pgettext('BillingDateRule', 'Select predefined rule to add.')
          "
        >
        </ItpFormSelect2>
        <ItpButton
          variant="light"
          icon="fa-plus"
          :tooltip="pgettext('BillingDateRule', 'Add rule configuration')"
          :disabled="!selectedRuleID"
          @click="evh_6705964765214521_click($event, {})"
        >
        </ItpButton>
      </div>
    </div>
  </div>
  <div class="row mb-3" v-if="loaded">
    <div class="form-group col-md-8">
      <ItpFormSelect2
        name="billing_date_rule_anchor"
        v-model="item.anchor"
        :value="item.anchor"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.anchor =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="anchorOptions"
        valueField="value"
        textField="name"
        required
        :readonly="readonly"
      >
      </ItpFormSelect2>
    </div>
    <div class="form-group col-md-4">
      <div class="input-group">
        <div class="input-group-text">
          <input
            type="checkbox"
            v-model="isOffsetDaysSet"
            :readonly="readonly"
            @change="evh_4063326939524293_change($event, {})"
          />
        </div>
        <ItpFormInput
          type="number"
          name="offset_days"
          v-model="item.offset_days"
          :value="item.offset_days"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.offset_days =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :disabled="!isOffsetDaysSet"
          :readonly="readonly"
          ref="offsetDays"
        >
        </ItpFormInput>
        <span class="input-group-text">
          {{ pgettext('InputGroupAddon', 'day') }}
        </span>
      </div>
    </div>
  </div>
  <div class="row" v-if="loaded">
    <div class="col">
      <ItpFormInput
        name="description"
        v-model="item.description"
        :value="item.description"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.description =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :placeholder="pgettext('BillingDateRule', 'Rule description')"
        :readonly="readonly"
        v-if="loaded"
      >
      </ItpFormInput>
    </div>
  </div>
</template>
